/** XeAgents.ai**/
import { useState, useEffect, useRef, RefObject } from "react";
import { TabConfig } from "./useCaseData";

interface UseScrollBasedTabsOptions {
  tabsConfig: TabConfig[];
  isMobile: boolean;
  defaultTab: string;
}

export function useScrollBasedTabs({
  tabsConfig,
  isMobile,
  defaultTab,
}: UseScrollBasedTabsOptions) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;

      const scrollableHeight = sectionHeight - viewportHeight;
      const scrollProgress = Math.max(
        0,
        Math.min(1, -rect.top / scrollableHeight)
      );

      const tabIndex = Math.min(
        Math.floor(scrollProgress * tabsConfig.length),
        tabsConfig.length - 1
      );

      const newActiveTab = tabsConfig[tabIndex]?.id || defaultTab;
      setActiveTab(newActiveTab);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, tabsConfig, defaultTab]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);

    if (isMobile || !sectionRef.current) return;

    const section = sectionRef.current;
    const sectionTop = section.offsetTop;
    const sectionHeight = section.offsetHeight;
    const viewportHeight = window.innerHeight;
    const scrollableHeight = sectionHeight - viewportHeight;

    const tabIndex = tabsConfig.findIndex((t) => t.id === tabId);
    const targetProgress = tabIndex / tabsConfig.length;
    const targetScroll = sectionTop + scrollableHeight * targetProgress;

    window.scrollTo({
      top: targetScroll,
      behavior: "smooth",
    });
  };

  return {
    activeTab,
    sectionRef,
    handleTabClick,
  };
}

export function useResponsive() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

// The pinned, scroll-to-switch-tabs layout needs a viewport tall enough to hold
// the heading, tabs and two rows of cards (~950px). Laptops and tablets get the
// normal-flow layout with click-to-switch tabs instead of clipped content.
export function useStickyTabs() {
  const [sticky, setSticky] = useState(false);

  useEffect(() => {
    const check = () => {
      setSticky(window.innerWidth >= 1024 && window.innerHeight >= 950);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return sticky;
}
